import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  Text,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FaDownload, FaStickyNote } from 'react-icons/fa';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderSortCell,
  TableSortDirection,
  IconButton,
  useNexus,
  BodyText,
} from '@spotoninc/nexus-react';
import { DownloadIcon } from '@spotoninc/nexus-web-assets';
import { fetchWithAuth } from '../../../utils/apiHelpers';
import { downloadExcel } from 'react-export-table-to-excel';
import './reskinnedBenchmarkTable.css';

const ReskinnedBenchmarkTable = props => {
  // Get Nexus theme context
  const { theme } = useNexus();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // Function to sort data
  const getSortedData = data => {
    if (!sortColumn || !data) return data;

    return [...data].sort((a, b) => {
      let x = a[sortColumn];
      let y = b[sortColumn];

      // Handle string values that might be numbers
      if (typeof x === 'string') {
        if (!isNaN(parseFloat(x))) {
          x = parseFloat(x);
        } else if (sortColumn !== 'expense_head') {
          x = x.charCodeAt(0);
        }
      }

      if (typeof y === 'string') {
        if (!isNaN(parseFloat(y))) {
          y = parseFloat(y);
        } else if (sortColumn !== 'expense_head') {
          y = y.charCodeAt(0);
        }
      }

      // Sort based on direction
      return sortDirection === TableSortDirection.ascending ? x - y : y - x;
    });
  };

  // Handle sort column change
  const handleSort = (column, direction) => {
    setLoading(true);

    // If sorting by expense_head, call the parent's callTable function
    if (column === 'expense_head') {
      props.callTable();
    }

    // Update sort state
    setSortColumn(column);
    setSortDirection(direction);

    // Simulate loading state
    setTimeout(
      () => {
        setLoading(false);
      },
      column === 'expense_head' ? 1000 : 700
    );
  };

  // Function to handle Excel download
  const handleDownloadExcel = () => {
    const headers = [
      'Category',
      'Average Business',
      'Best in Class',
      'Your Business',
    ];
    const body = props.table.map(row => [
      row.expense_head,
      row.avg_in_class + '%',
      row.best_in_class + '%',
      row.metric + '%',
    ]);

    downloadExcel({
      fileName: 'Benchmark',
      sheet: 'Benchmark',
      tablePayload: {
        header: headers,
        body: body,
      },
    });
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchWithAuth('/api/benchmark_data/', {
      method: 'POST',
    })
      .then(response => response.json())
      .then(data => {
        if (data.code === undefined) {
          setData(data['table']);
        } else {
          window.open('/', '_self');
          alert('Session Expired!.');
        }
      })
      .catch(err => {
        console.error(err);
      });
  }, []);

  // Handle click on category
  const handleCategoryClick = category => {
    props.clickThru('cost', category);
  };

  return (
    <Card width={'100%'}>
      <CardHeader>
        <Flex>
          <Flex gap={2} flex={1} alignItems={'center'} width={'100%'}>
            <Text
              color="var(--Text-neutral-strongest, #0A091B)"
              fontFeatureSettings="'liga' off"
              fontFamily="var(--font-family-sans, Poppins)"
              fontSize="var(--font-size-200, 1.125rem)"
              fontStyle="normal"
              fontWeight={600}
              lineHeight="var(--font-line-height-300, 1.5rem)"
            >
              Benchmark
            </Text>
          </Flex>
          <Flex flex={1} justifyContent={'flex-end'}>
            <IconButton
              icon={DownloadIcon}
              label="Download"
              variant="tonal"
              onClick={handleDownloadExcel}
            />
          </Flex>
        </Flex>
      </CardHeader>
      <CardBody overflowX={'auto'}>
        {/* Wrap the table in the Nexus theme context to ensure design tokens work properly */}
        <div className={theme.className}>
          <Table className="nexus-benchmark-table">
            <TableHead sticky>
              <TableRow variant="filled">
                <TableHeaderSortCell
                  onSort={direction => handleSort('expense_head', direction)}
                  direction={
                    sortColumn === 'expense_head' ? sortDirection : undefined
                  }
                >
                  <BodyText size="medium" style={{ fontWeight: '600' }}>
                    Category
                  </BodyText>
                </TableHeaderSortCell>
                <TableHeaderSortCell
                  textAlign="left"
                  onSort={direction => handleSort('avg_in_class', direction)}
                  direction={
                    sortColumn === 'avg_in_class' ? sortDirection : undefined
                  }
                >
                  <BodyText size="medium" style={{ fontWeight: '600' }}>
                    Average Business
                  </BodyText>
                </TableHeaderSortCell>
                <TableHeaderSortCell
                  textAlign="left"
                  onSort={direction => handleSort('best_in_class', direction)}
                  direction={
                    sortColumn === 'best_in_class' ? sortDirection : undefined
                  }
                >
                  <BodyText size="medium" style={{ fontWeight: '600' }}>
                    Best in Class
                  </BodyText>
                </TableHeaderSortCell>
                <TableHeaderSortCell
                  textAlign="left"
                  onSort={direction => handleSort('metric', direction)}
                  direction={
                    sortColumn === 'metric' ? sortDirection : undefined
                  }
                >
                  <BodyText size="medium" style={{ fontWeight: '600' }}>
                    Your Business
                  </BodyText>
                </TableHeaderSortCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.table &&
                getSortedData(props.table).map((row, index) => (
                  <TableRow key={index} variant="bottom-divider">
                    <TableCell
                      onClick={() => handleCategoryClick(row.expense_head)}
                      style={{
                        cursor: 'pointer',
                        color: '#0A091B',
                        fontWeight: 400,
                      }}
                    >
                      <BodyText size="medium">{row.expense_head}</BodyText>
                    </TableCell>
                    <TableCell textAlign="left">
                      <BodyText size="medium">
                        {row.avg_in_class === '-'
                          ? '0%'
                          : `${row.avg_in_class}%`}
                      </BodyText>
                    </TableCell>
                    <TableCell textAlign="left">
                      <BodyText size="medium">
                        {row.best_in_class === '-'
                          ? '0%'
                          : `${row.best_in_class}%`}
                      </BodyText>
                    </TableCell>
                    <TableCell textAlign="left" className="your-business-cell">
                      <BodyText size="medium" emphasis>
                        {row.metric === '-' ? '0%' : `${row.metric}%`}
                      </BodyText>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default ReskinnedBenchmarkTable;
